import {
  Box,
  CircularProgress,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import storeConnector from '@src/store/storeConnector';
import { useCallback, useState, memo, useEffect } from 'react';
import useSubscription from '@src/hooks/useSubscription';
import { DataPoint } from '@src/types/DataPoint';
import SimplePointDataTable from './SimplePointDataTable';
import { WidgetTypePointList } from '@src/types/Widget';
import useGetInternalWidgetConfig from '@src/hooks/api/queries/useGetInternalWidgetConfig';
import useGetDevicesWidgetConfigs, {
  DeviceWidgetOption,
} from '@src/hooks/api/queries/useGetDevicesWidgetConfigs';

const MeterPointList = memo(({ dataPoints }: { dataPoints: DataPoint[] }) => {
  const dpWithValues = useSubscription(dataPoints);
  return (
    <Box mt={2}>
      <SimplePointDataTable data={dpWithValues} />
    </Box>
  );
});

const Meter = memo(() => {
  const {
    data: meterOptions,
    isFetching,
    isLoading,
  } = useGetDevicesWidgetConfigs('meter');

  const [selectedMeter, setSelectedMeter] = useState<DeviceWidgetOption>(
    meterOptions?.[0] ?? {
      name: '',
      engineeringId: '',
      deviceId: '',
    },
  );

  const findMeterById = useCallback(
    (id: string) => {
      return meterOptions.find((m) => m.engineeringId === id);
    },
    [meterOptions],
  );

  const {
    data: widgetConfig,
    isFetching: isMetersWidgetFetching,
    isLoading: isMetersWidgetLoading,
  } = useGetInternalWidgetConfig<WidgetTypePointList>(
    `meterPoints_${selectedMeter.engineeringId}`,
    {
      sourceDeviceId: selectedMeter?.engineeringId,
    },
    {
      enabled: !!meterOptions?.length && !!selectedMeter?.engineeringId,
      queryKeys: ['meterPoints', selectedMeter?.engineeringId || ''],
    },
  );

  const handleMeterChange = useCallback(
    (e: SelectChangeEvent<string>) => {
      const foundMeter = findMeterById(e.target.value);

      if (
        foundMeter &&
        foundMeter.engineeringId !== selectedMeter.engineeringId
      ) {
        setSelectedMeter(foundMeter);
      }
    },
    [selectedMeter.engineeringId, findMeterById],
  );

  useEffect(() => {
    if (meterOptions?.length) {
      setSelectedMeter(meterOptions[0]);
    }
  }, [meterOptions]);

  if (
    isFetching ||
    isMetersWidgetFetching ||
    isLoading ||
    isMetersWidgetLoading
  ) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {meterOptions?.length ? (
        <Select
          onChange={handleMeterChange}
          value={selectedMeter.engineeringId}
          variant='standard'
          renderValue={(selected) =>
            findMeterById(selected)?.name ?? 'Select Meter'
          }
          displayEmpty
          disableUnderline
          sx={{
            fontSize: '1.2rem',
          }}
        >
          {meterOptions?.map((meter) => (
            <MenuItem key={meter.deviceId} value={meter.engineeringId}>
              {meter.name}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <h4>POI Meter</h4>
      )}
      {widgetConfig?.dataPoints && widgetConfig.dataPoints.length > 0 ? (
        <MeterPointList dataPoints={widgetConfig.dataPoints[0]} />
      ) : (
        <Box mt={2}>
          <Typography variant='body1' align='center'>
            No meter configuration found
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default storeConnector(Meter, {
  user: ['role'],
  config: ['siteMeta'],
});
